import { useIsCopilotEnabled } from '@common/Copilot/useIsCopilotEnabled';
import { FabricSetupButton } from '@common/Fabric/FabricSetup';
import { PageHeader } from '@common/Header/PageHeader';
import { IS_ONBOARDING_DISABLED } from '@common/onboarding/constants';
import { useOnboardingDetail } from '@common/onboarding/hooks';
import { UserIndustry } from '@common/onboarding/userConstants';
import { HUB_URL } from '@common/packageHub/constants';
import { QueryKeys } from '@common/queries/common';
import { HomePagePipelinesQuery } from '@common/queries/pipeline';
import { Entity } from '@common/types/Entity';
import { getIDEUrl, getSqlIDEUrl, Private_Routes, Public_Routes } from '@common/url';
import { EntityEvents } from '@common/user-analytics';
import { GetUserOnboardingDetails } from '@pages/onboarding/query';
import { ResumeReplayBanner } from '@pages/Sql/Replay/components/ResumeReplayBanner';
import { BackEndCodeLanguage } from '@prophecy/graphql-types/enums';
import {
  ArrowNarrowDownIcon,
  ArrowNarrowUpRightIcon,
  ClockIcon,
  ContainerIcon,
  GemIcon,
  LineageIcon,
  HexagonBIcon,
  MetadataIcon,
  PipelineIcon,
  SearchSmIcon,
  UserAIcon,
  HomeLineIcon
} from '@prophecy/icons';
import { BaseColor, theme } from '@prophecy/theme';
import { Badges, Button, Card, Container, Ellipsis, FeaturedIcon, Input, Spinner, Stack, Text } from '@prophecy/ui-v3';
import { Variant as FeaturedIconVariant } from '@prophecy/ui-v3/Misc/FeaturedIcon/types';
import { getDayPart } from '@prophecy/utils/date';
import { EXPENSIVE_CSS_CLS } from '@prophecy/utils/expensiveCss';
import { VariablesOf } from 'gql.tada';
import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { ReactComponent as DocumentationHome } from '../../assets/home/documentation.svg?react';
import { ReactComponent as ExpressEdition } from '../../assets/home/express.svg?react';
import { ReactComponent as University } from '../../assets/home/prophecy_university.svg?react';
import { useEditionConfig, useSupportedLanguages } from '../../context/edition';
import { useGlobalVariables } from '../../context/globalConfig/securedConfigHook';
import { useMixPanel } from '../../context/mixpanel/context';
import { UserProviderType, useUser } from '../../context/user';
import { useGraphQlQuery } from '../../data/util';
import { getEntityTableSortConfigVariables } from './components/Config';

const StyledHomeContainer = styled(Stack)`
  background: ${theme.colors.gray50};
  padding-bottom: ${theme.spaces.x40};
  overflow: auto;
`;

const StyledContainer = styled(Container)`
  overflow: visible;
`;

const StyledVideoContainer = styled(Stack)`
  aspect-ratio: 16/9;
`;

const StyledText = styled(Text)`
  padding-top: ${theme.spaces.x24};
`;

const StyledStack = styled(Stack)`
  font-size: ${theme.fontSizes.x14};
  padding: ${theme.spaces.x4} ${theme.spaces.x8};
  color: ${theme.colors.gray500};
  border-radius: ${theme.radius.x6};
  transition: ${theme.transition.s};
  .projectName {
    visibility: hidden;
    font-weight: inherit;
    max-width: 200px;
    font-size: ${theme.fontSizes.x12};
    color: ${theme.colors.gray400};
  }
  .viewAll {
    color: ${theme.colors.brand700};
  }
  &:hover {
    color: ${theme.colors.brand700};
    font-weight: ${theme.fontWeight.semiBold};
    background: ${theme.colors.white};
    .projectName {
      visibility: visible;
    }
  }
`;

const StyledDownArrowIcon = styled(ArrowNarrowDownIcon)`
  transform: rotate(-90deg);
  color: ${theme.colors.brand700};
`;
const StyledNavCard = styled(Card)`
  width: 225px;
  padding: ${theme.spaces.x12} ${theme.spaces.x24};
  transition: ${theme.transition.m};

  .${EXPENSIVE_CSS_CLS} & {
    box-shadow: ${theme.shadows.sm} !important;
    border: none !important;
  }
`;

const StyledNavButton = styled(Button)<{ to: string }>`
  color: ${theme.colors.gray500};
  &:hover:not([disabled]) {
    color: ${theme.colors.gray900};
    font-weight: ${theme.fontWeight.semiBold};
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

const StyledStartedNavCard = styled(Card)<{ disabled: boolean }>`
  background: inherit;
  width: 400px;
  transition: ${theme.transition.m};
  .${EXPENSIVE_CSS_CLS} & {
    box-shadow: ${theme.shadows.sm} !important;
    border: none !important;
  }
  ${({ disabled }) => (disabled ? `box-shadow: ${theme.shadows.sm};` : '')}
  &:hover a {
    color: ${theme.colors.gray900};
    font-weight: ${theme.fontWeight.semiBold};
  }
  &:hover p {
    color: ${theme.colors.gray500};
    font-weight: normal;
  }
`;

const StyledNavText = styled.p`
  color: ${theme.colors.gray500};
`;

const StyledStartedNavButton = styled.a`
  color: ${theme.colors.gray900};
`;
const StyledFooterCard = styled(Card)<{ disabled: boolean }>`
  padding: ${theme.spaces.x24};
  .${EXPENSIVE_CSS_CLS} & {
    box-shadow: ${theme.shadows.sm} !important;
    border: none !important;
  }
  ${({ disabled }) => (disabled ? `margin-bottom: ${theme.spaces.x40};` : '')}
  box-shadow: none;
  &:hover {
    box-shadow: none;
  }
`;

const StyledGrid = styled(Stack)<{ disabled: boolean }>`
  ${({ disabled }) =>
    disabled
      ? `
   order: 3;
   margin-bottom: ${theme.spaces.x40};
   pointer-events: none;
   button, a{
    cursor: not-allowed;
   }
  `
      : ''}
`;

const WistiaVideoElement = styled.iframe`
  width: 100%;
  height: 100%;
  border-radius: ${theme.spaces.x6};
`;

const NavBadges = styled(Badges)`
  cursor: pointer;
` as typeof Badges;
const PIPELINE_LIMIT = 5;

const NavCard = ({
  icon,
  label,
  to,
  disabled,
  dataTestId
}: {
  icon: React.ReactNode;
  label: string;
  to: string;
  disabled: boolean;
  dataTestId: string;
}) => {
  return (
    <StyledNavButton
      variant='plain'
      elementType={disabled ? 'button' : 'link'}
      to={disabled ? '' : to}
      disabled={disabled}
      data-test-id={dataTestId}>
      <StyledNavCard disabled={disabled}>
        <Stack direction='horizontal' gap={theme.spaces.x8} alignY='center'>
          {icon} {label}
        </Stack>
      </StyledNavCard>
    </StyledNavButton>
  );
};

const StartedNavCard = ({
  icon,
  label,
  subTitle,
  href,
  disabled
}: {
  icon: React.ReactNode;
  label: string;
  subTitle: string;
  href: string;
  disabled: boolean;
}) => {
  return (
    <StyledStartedNavCard hasHoverEffect={true} disabled={disabled}>
      <StyledStartedNavButton
        href={href}
        target='_blank'
        rel='noreferrer'
        data-test-id={`metadata-home-${label.toLowerCase().replace(/\s+/g, '-')}-link`}>
        <Stack direction='horizontal' gap={theme.spaces.x12} alignY='center'>
          {icon}
          <Stack gap={theme.spaces.x4}>
            <Text level='md'>{label}</Text>
            <StyledNavText>{subTitle}</StyledNavText>
          </Stack>
        </Stack>
      </StyledStartedNavButton>
    </StyledStartedNavCard>
  );
};

export default function Home() {
  const { user } = useUser() as unknown as UserProviderType;

  const { pipeline: onboardingPipeline, sqlProject, analystProject } = useOnboardingDetail();
  const { isSapEnabled } = useGlobalVariables();

  const navigate = useNavigate();
  const uid = user?.id;
  const { track } = useMixPanel();

  useEffect(() => {
    track(EntityEvents[Entity.User].metadata.homeOpen, { id: uid, name: user?.firstName, email: user?.email });
  }, [track, uid, user?.firstName, user?.email]);

  const pipelineConfigVariables = getEntityTableSortConfigVariables(Entity.Pipeline);
  const variables = { uid, ...pipelineConfigVariables } as VariablesOf<typeof HomePagePipelinesQuery>;
  const globalConf = useGlobalVariables();
  const isCopilotEnabled = useIsCopilotEnabled();
  const isFabricSet = globalConf.getFabricStatus as boolean;
  const { data, isLoading } = useGraphQlQuery(
    [QueryKeys.Pipeline, 'UserDetail', variables],
    HomePagePipelinesQuery,
    undefined,
    variables
  );
  const isOnboardingV2Enabled = globalConf.isOnboardingV2Enabled;
  const editionConfig = useEditionConfig();
  const pipelineList = useMemo(() => {
    const result: {
      created: number;
      project: {
        name: string;
        _id: string;
      };
      name: string;
      _id: string;
      isSql?: boolean;
    }[] = [];

    if (data?.User) {
      const { pipelines, sqlOrchestratorPipelines } = data.User;
      result.push(...pipelines);
      sqlOrchestratorPipelines?.forEach((pipeline) => {
        result.push({ ...pipeline, isSql: true });
      });
      result.sort((a, b) => b.created - a.created);
    }

    return result.slice(0, PIPELINE_LIMIT);
  }, [data?.User]);

  const { data: userOnboardingDetails } = useGraphQlQuery(
    [QueryKeys.UserOnboardingDetails, 'UserDetail', variables],
    GetUserOnboardingDetails,
    undefined,
    variables
  );

  const computeOnboardingUrl = () => {
    if (IS_ONBOARDING_DISABLED || editionConfig.expressEdition) return undefined;
    const canDoSql = isCopilotEnabled && sqlProject;
    const canDoSpark = Boolean(onboardingPipeline);

    const analystProjectId = analystProject?._id || globalConf.getAnalystProjectId;

    const pipelineUrl = canDoSpark
      ? getIDEUrl({ entity: Entity.Pipeline, uid: onboardingPipeline?._id as string })
      : undefined;
    const onboardingHomeUrl = Private_Routes.Onboarding.getUrl();

    if (isOnboardingV2Enabled) {
      const { industry } = userOnboardingDetails?.User || {};

      // Redirect to template hub if the industry is OTHER since backend considers each category as a different industry and creates a new project for each one which is not intended for "Other" category
      if (industry === UserIndustry.OTHER || industry === UserIndustry.HOSPITALITY) {
        return HUB_URL.TEMPLATE;
      }

      if (analystProjectId) {
        return getSqlIDEUrl({ uid: analystProjectId });
      }
      // This will only happen for the users who signed up before the onboarding v2 was enabled
      // since analyst project is created for every category except OTHER and HOSPITALITY for new users
      if (canDoSql) {
        return onboardingHomeUrl;
      }
      return pipelineUrl;
    }

    if (canDoSql) {
      return onboardingHomeUrl;
    }

    return pipelineUrl;
  };

  const onboardingUrl = computeOnboardingUrl();

  const onboardingButton = onboardingUrl ? (
    <NavBadges
      size='l'
      tone={BaseColor.greenLight}
      elementType={'link'}
      icon={<ArrowNarrowUpRightIcon type='default' />}
      iconPlacement='left'
      to={onboardingUrl}>
      Onboarding
    </NavBadges>
  ) : null;

  const getPipelineList = () => {
    return pipelineList.map((p) => {
      const toUrl = p.isSql
        ? getSqlIDEUrl({ uid: p.project._id, entity: Entity.Pipeline.toLowerCase(), name: p.name })
        : getIDEUrl({ uid: p._id, entity: Entity.Pipeline });

      return (
        <Button key={p._id} variant='plain' elementType={'link'} to={toUrl} data-test-id='metadata-home-project-button'>
          <StyledStack alignY='center' gap={theme.spaces.x4} direction='horizontal' align='space-between'>
            <Ellipsis>{p.name}</Ellipsis>
            <Text level='sm' className='projectName'>
              <Ellipsis>{p?.project?.name}</Ellipsis>
            </Text>
          </StyledStack>
        </Button>
      );
    });
  };

  const onSubmit = (value: string) => {
    navigate(Private_Routes.Lineage_Search.getUrl(undefined, { search: value }));
  };

  const supportedLanguages = useSupportedLanguages();
  const isSparkSupported =
    supportedLanguages.has(BackEndCodeLanguage.python) || supportedLanguages.has(BackEndCodeLanguage.scala);

  return (
    <StyledHomeContainer width='100%' height='100%' alignY='center'>
      <PageHeader crumbs={[{ label: 'Home', icon: <HomeLineIcon type='default' /> }]} />
      <StyledContainer>
        <Stack gap={theme.spaces.x24}>
          <Stack direction='horizontal' align='space-between' padding={`${theme.spaces.x24} 0 0`}>
            <Text level='xl' weight={theme.fontWeight.semiBold}>
              {`Good ${getDayPart()} ${user?.firstName}`}
            </Text>
            {isFabricSet && (
              <Stack width='45%'>
                <Input
                  placeholder='Search...'
                  icon={<SearchSmIcon type='default' />}
                  onPressEnter={(e) => onSubmit(e?.currentTarget?.value)}
                  data-test-id='metadata-home-search-input'
                />
              </Stack>
            )}
          </Stack>
          <ResumeReplayBanner />
          {!isFabricSet && <FabricSetupButton />}

          <StyledGrid disabled={!isFabricSet} gap={theme.spaces.x24}>
            <Stack direction='horizontal' gap={theme.spaces.x24} align='space-between'>
              <Stack gap={theme.spaces.x12} width={isFabricSet ? '700px' : '100%'}>
                <StyledText level='sm' tone={theme.colors.gray400} weight={theme.fontWeight.normal}>
                  Shortcuts
                </StyledText>
                <Stack width='100%' direction='horizontal' gap={theme.spaces.x12} wrap>
                  {isSparkSupported && (
                    <>
                      <NavCard
                        icon={
                          <FeaturedIcon
                            size='l'
                            tone={BaseColor.secondary}
                            variant={FeaturedIconVariant.lightGradient}
                            icon={<PipelineIcon type='extended' />}
                          />
                        }
                        label={`New ${Entity.Pipeline}`}
                        to={Private_Routes.Create_Entity.tab.getUrl({ tab: Entity.Pipeline.toLowerCase() })}
                        disabled={!isFabricSet}
                        dataTestId={`metadata-home-nav-button-${Entity.Pipeline.toLowerCase()}-button`}
                      />
                      <NavCard
                        icon={
                          <FeaturedIcon
                            size='l'
                            tone={BaseColor.blue}
                            variant={FeaturedIconVariant.lightGradient}
                            icon={<HexagonBIcon type='default' />}
                          />
                        }
                        label={`See ${Entity.Dataset}s`}
                        to={Private_Routes.User.tab.getUrl({ tab: Entity.Dataset.toLowerCase() + 's' })}
                        disabled={!isFabricSet}
                        dataTestId={`metadata-home-nav-button-${Entity.Dataset.toLowerCase()}-button`}
                      />
                    </>
                  )}

                  <NavCard
                    icon={
                      <FeaturedIcon
                        size='l'
                        tone={BaseColor.orange}
                        variant={FeaturedIconVariant.lightGradient}
                        icon={<UserAIcon type='default' />}
                      />
                    }
                    label={`Manage ${Entity.Team}`}
                    to={Private_Routes.User.tab.getUrl({ tab: `${Entity.Team.toLowerCase()}s` })}
                    disabled={!isFabricSet}
                    dataTestId={`metadata-home-nav-button-${Entity.Team.toLowerCase()}-button`}
                  />

                  <NavCard
                    icon={
                      <FeaturedIcon
                        size='l'
                        tone={BaseColor.primary}
                        variant={FeaturedIconVariant.lightGradient}
                        icon={<ContainerIcon type='default' />}
                      />
                    }
                    label={`New ${Entity.Project}`}
                    to={Private_Routes.Create_Entity.tab.getUrl({ tab: Entity.Project.toLowerCase() })}
                    disabled={!isFabricSet}
                    dataTestId={`metadata-home-nav-button-${Entity.Project.toLowerCase()}-button`}
                  />
                  <NavCard
                    icon={
                      <FeaturedIcon
                        size='l'
                        tone={BaseColor.success}
                        variant={FeaturedIconVariant.lightGradient}
                        icon={<PipelineIcon type='extended' />}
                      />
                    }
                    label={`See ${Entity.Pipeline}s`}
                    to={Private_Routes.User.tab.getUrl({ tab: `${Entity.Pipeline.toLowerCase()}s` })}
                    disabled={!isFabricSet}
                    dataTestId={`metadata-home-nav-button-${Entity.Pipeline.toLowerCase()}-button`}
                  />
                  <NavCard
                    icon={
                      <FeaturedIcon
                        size='l'
                        tone={BaseColor.primary}
                        variant={FeaturedIconVariant.lightGradient}
                        icon={<MetadataIcon type='extended' />}
                      />
                    }
                    label='View Metadata'
                    to={Private_Routes.User.home.getUrl()}
                    disabled={!isFabricSet}
                    dataTestId={`metadata-home-nav-button-metadata-button`}
                  />
                  {isSparkSupported && (
                    <>
                      <NavCard
                        icon={
                          <FeaturedIcon
                            size='l'
                            tone={BaseColor.warning}
                            variant={FeaturedIconVariant.lightGradient}
                            icon={<LineageIcon type='extended' />}
                          />
                        }
                        label='See Lineage'
                        to={Private_Routes.Lineage_Search.getUrl(undefined)}
                        disabled={!isFabricSet}
                        dataTestId={`metadata-home-nav-button-${Entity.Lineage.toLowerCase()}-button`}
                      />
                      <NavCard
                        icon={
                          <FeaturedIcon
                            size='l'
                            tone={BaseColor.grayBlue}
                            variant={FeaturedIconVariant.lightGradient}
                            icon={<ClockIcon type='default' />}
                          />
                        }
                        label='New Job'
                        to={Private_Routes.Create_Entity.tab.getUrl({ tab: Entity.Job.toLowerCase() })}
                        disabled={!isFabricSet}
                        dataTestId={`metadata-home-nav-button-${Entity.Job.toLowerCase()}-button`}
                      />
                      <NavCard
                        icon={
                          <FeaturedIcon
                            size='l'
                            tone={BaseColor.pink}
                            variant={FeaturedIconVariant.lightGradient}
                            icon={<GemIcon type='extended' />}
                          />
                        }
                        label='Gem Builder'
                        to={Private_Routes.Gems.getUrl()}
                        disabled={!isFabricSet || !globalConf.isPlaygroundEnabled}
                        dataTestId={`metadata-home-nav-button-${Entity.Gem.toLowerCase()}-button`}
                      />
                    </>
                  )}
                </Stack>
              </Stack>
              {isFabricSet && (
                <Stack gap={theme.spaces.x12} width='30%'>
                  <StyledText level='sm' tone={theme.colors.gray400} weight={theme.fontWeight.normal}>
                    Recent Pipelines
                  </StyledText>
                  <Stack gap={theme.spaces.x12}>
                    {isLoading ? (
                      <Spinner spinning />
                    ) : (
                      <>
                        {getPipelineList()}
                        <Button
                          variant='plain'
                          elementType='link'
                          to={Private_Routes.User.tab.getUrl({ tab: `${Entity.Pipeline.toLowerCase()}s` })}
                          data-test-id='metadata-home-view-all-pipelines-button'>
                          <StyledStack direction='horizontal' alignY='center' gap={theme.spaces.x8}>
                            <Text level='sm' className='viewAll'>
                              View All
                            </Text>
                            <StyledDownArrowIcon type='default' />
                          </StyledStack>
                        </Button>
                      </>
                    )}
                  </Stack>
                </Stack>
              )}
            </Stack>
          </StyledGrid>
          <Stack gap={theme.spaces.x24}>
            <Stack direction='horizontal' align='space-between' padding={`${theme.spaces.x24} 0 0 0 `}>
              <Text level='sm' tone={theme.colors.gray400} weight={theme.fontWeight.normal}>
                Get started with Prophecy
              </Text>
              {onboardingButton}
            </Stack>

            <StyledFooterCard disabled={isFabricSet}>
              <Stack direction='horizontal' gap={theme.spaces.x24} align='space-between'>
                <Stack gap={theme.spaces.x8} width='38%'>
                  {editionConfig.expressEdition && (
                    <StartedNavCard
                      disabled={!isFabricSet}
                      href='https://docs.prophecy.ai/data-analysis/administration/setup/prophecy-express-guide'
                      icon={<ExpressEdition />}
                      label='Express Edition'
                      subTitle='Explore the documentation'
                    />
                  )}
                  {!isSapEnabled && (
                    <StartedNavCard
                      disabled={!isFabricSet}
                      href='https://www.prophecy.io/prophecy-university'
                      icon={<University />}
                      label='Join Prophecy University'
                      subTitle='Explore all features'
                    />
                  )}
                  <StartedNavCard
                    disabled={!isFabricSet}
                    href={isSapEnabled ? 'https://help.sap.com/docs/SPM_DIaddon' : Public_Routes.ProphecyDoc.getUrl()}
                    icon={<DocumentationHome />}
                    label='Become a Pro'
                    subTitle='Explore the documentation'
                  />
                </Stack>
                <Stack width='60%'>
                  <StyledVideoContainer width='100%'>
                    <WistiaVideoElement
                      src='//fast.wistia.net/embed/iframe/tbge2mermg'
                      allowTransparency
                      title="Prophecy 3.2: What's new?"
                      frameBorder='0'
                      scrolling='no'
                      className='wistia_embed'
                      name='wistia_embed'
                      allowFullScreen
                      width='100%'
                      height='100%'
                    />
                    <script src='//fast.wistia.net/assets/external/E-v1.js' async></script>
                  </StyledVideoContainer>
                </Stack>
              </Stack>
            </StyledFooterCard>
          </Stack>
        </Stack>
      </StyledContainer>
    </StyledHomeContainer>
  );
}
